import { useCallback, useMemo, useState } from 'react';
import { Badge, Button, InputNumber, Select, Space } from 'antd';
import { FmsAccountManagement } from 'types/fms';
import styles from './index.module.scss';
import classNames from 'classnames';
import { EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { useToggle } from 'react-use';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import { pickAndUploadReceiptImage } from 'utils/receipt-image-upload';
import { ReceiptImagesManageModal } from 'components/receipt-images-manage-modal';

interface CollectionInputType {
  payAmount: number | null;
  payType: string;
  receiptKeys: string[];
}

interface Props {
  accountList: FmsAccountManagement[];
  onChange?: (data: Partial<CollectionInputType>) => void;
  value?: CollectionInputType;
  onBlur?: () => void;
  max?: number;
}

const PaymentInput = ({ onChange, value, onBlur, max, accountList }: Props) => {
  const [uploading, toggleUploading] = useToggle(false);
  const [viewOpen, setViewOpen] = useState(false);
  const receiptKeys = useMemo(
    () => value?.receiptKeys || [],
    [value?.receiptKeys],
  );

  const triggerChange = useCallback(
    (changedValue: Partial<CollectionInputType>) => {
      onChange?.({
        ...value,
        ...changedValue,
      });
    },
    [value, onChange],
  );

  const updateReceiptKeys = useCallback(
    (keys: string[]) => {
      triggerChange({ receiptKeys: keys });
    },
    [triggerChange],
  );

  const CollectionTypeSelectEle = useMemo(() => {
    return (
      <Select
        value={value?.payType}
        className={classNames(styles.marginZero, 'w-56')}
        options={accountList.map((i) => {
          return {
            value: i.accountCode,
            label: i.accountName,
          };
        })}
        onChange={(accountCode: string) => {
          triggerChange({
            payType: accountCode,
          });
        }}
      />
    );
  }, [value?.payType, accountList, triggerChange]);

  const handleUpload = useCallback(async () => {
    toggleUploading(true);
    try {
      const key = await pickAndUploadReceiptImage();
      if (key) {
        updateReceiptKeys([...receiptKeys, key]);
      }
    } finally {
      toggleUploading(false);
    }
  }, [toggleUploading, updateReceiptKeys, receiptKeys]);

  return (
    <>
      <InputNumber
        className="w-full"
        addonBefore={CollectionTypeSelectEle}
        value={value?.payAmount}
        onChange={(e) => {
          triggerChange({ payAmount: e });
        }}
        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
        placeholder=""
        onBlur={() => onBlur?.()}
        max={max}
        addonAfter={
          <Space>
            <Button
              type="link"
              size="small"
              title={String(i18n.t(LOCALS.payment_receipt_image_upload))}
              onClick={handleUpload}
              disabled={uploading}
              loading={uploading}
              icon={<UploadOutlined />}
            />
            <Badge count={receiptKeys.length} size="small" offset={[-2, 2]}>
              <Button
                type="link"
                size="small"
                title={String(i18n.t(LOCALS.view))}
                icon={<EyeOutlined />}
                disabled={receiptKeys.length === 0}
                onClick={() => setViewOpen(true)}
              />
            </Badge>
          </Space>
        }
      />

      <ReceiptImagesManageModal
        open={viewOpen}
        receiptKeys={receiptKeys}
        onClose={() => setViewOpen(false)}
        onChange={updateReceiptKeys}
      />
    </>
  );
};

export default PaymentInput;
